import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  Calculator, Filter, X, Search, RefreshCcw, Layers,
} from "lucide-react";
import MultiSelect from "../components/MultiSelect";

const GROUPS = [
  { key: "brand_size", label: "Brand + Size" },
  { key: "brand", label: "Brand" },
  { key: "size", label: "Size" },
  { key: "party", label: "Customer" },
  { key: "godown", label: "Godown" },
  { key: "month", label: "Month" },
  { key: "none", label: "No grouping" },
];

export default function SaleCalculator() {
  const [godowns, setGodowns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [parties, setParties] = useState([]);

  const [brand, setBrand] = useState([]);
  const [size, setSize] = useState([]);
  const [godown, setGodown] = useState([]);
  const [partyIds, setPartyIds] = useState([]);
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(todayISO());
  const [groupBy, setGroupBy] = useState("brand_size");
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [g, b, s, p] = await Promise.all([
          api.get("/godowns"), api.get("/brands"), api.get("/sizes"),
          api.get("/parties?type=customer"),
        ]);
        setGodowns((g.data || []).filter((x) => x.kind !== "bullet"));
        setBrands(b.data || []); setSizes(s.data || []); setParties(p.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sale-calculator", {
        params: {
          brand: brand.length ? brand.join(",") : undefined,
          size: size.length ? size.join(",") : undefined,
          godown: godown.length ? godown.join(",") : undefined,
          party_id: partyIds.length ? partyIds.join(",") : undefined,
          date_from: from || undefined, date_to: to || undefined,
          group_by: groupBy,
        },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [brand, size, godown, partyIds, from, to, groupBy]);

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data?.groups || [];
    return (data?.groups || []).filter((g) => g.key.toLowerCase().includes(q));
  }, [data, search]);

  const summary = data?.summary || {};
  const clearFilters = () => { setBrand([]); setSize([]); setGodown([]); setPartyIds([]); setSearch(""); };
  const hasFilter = brand.length || size.length || godown.length || partyIds.length;
  const groupLabel = GROUPS.find((g) => g.key === groupBy)?.label || "";

  const quickRange = (days) => () => {
    const t = new Date();
    const f = new Date(); f.setDate(t.getDate() - days);
    setFrom(f.toISOString().slice(0, 10));
    setTo(t.toISOString().slice(0, 10));
  };
  const thisMonth = () => {
    const t = new Date();
    setFrom(new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10));
    setTo(todayISO());
  };
  const lastMonth = () => {
    const t = new Date();
    setFrom(new Date(t.getFullYear(), t.getMonth() - 1, 1).toISOString().slice(0, 10));
    setTo(new Date(t.getFullYear(), t.getMonth(), 0).toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-6" data-testid="sale-calc-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Calculator size={22} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Sale Calculator</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Compute sale totals by month · customer · brand · size · godown
        </span>
        <button onClick={load} className="ml-auto h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black" data-testid="sc-refresh">
          <RefreshCcw size={13} /> Refresh
        </button>
      </div>

      <div className="card-flat p-5 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <div>
            <label className="small-label block mb-1.5">Customer</label>
            <MultiSelect
              options={parties.map((p) => ({ value: p.id, label: p.name }))}
              values={partyIds}
              onChange={setPartyIds}
              placeholder="All customers"
              testid="sc-party" />
          </div>
          <div>
            <label className="small-label block mb-1.5">Brand</label>
            <MultiSelect
              options={brands.map((b) => ({ value: b.name, label: b.name }))}
              values={brand}
              onChange={setBrand}
              placeholder="All brands"
              testid="sc-brand" />
          </div>
          <div>
            <label className="small-label block mb-1.5">Size</label>
            <MultiSelect
              options={sizes.map((s) => ({ value: s.name, label: s.name }))}
              values={size}
              onChange={setSize}
              placeholder="All sizes"
              testid="sc-size" />
          </div>
          <div>
            <label className="small-label block mb-1.5">Godown</label>
            <MultiSelect
              options={godowns.map((g) => ({ value: g.name, label: g.name }))}
              values={godown}
              onChange={setGodown}
              placeholder="All godowns"
              testid="sc-godown" />
          </div>
          <div>
            <label className="small-label block mb-1.5">From</label>
            <input type="date" className="input-lg h-9 text-sm" value={from} onChange={(e) => setFrom(e.target.value)}
              data-testid="sc-from" />
          </div>
          <div>
            <label className="small-label block mb-1.5">To</label>
            <input type="date" className="input-lg h-9 text-sm" value={to} onChange={(e) => setTo(e.target.value)}
              data-testid="sc-to" />
          </div>
          <div>
            <label className="small-label block mb-1.5 flex items-center gap-1">
              <Layers size={11} /> Group by
            </label>
            <select className="input-lg h-9 text-sm" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              data-testid="sc-group">
              {GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500">
          <span>Quick:</span>
          <button onClick={thisMonth} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-this">This month</button>
          <button onClick={lastMonth} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-last">Last month</button>
          <button onClick={quickRange(7)} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-7">Last 7d</button>
          <button onClick={quickRange(30)} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-30">Last 30d</button>
          <button onClick={quickRange(90)} className="px-2 py-0.5 border border-gray-200 rounded-sm hover:border-black text-[11px]" data-testid="sc-q-90">Last 90d</button>
          {hasFilter && (
            <>
              <Filter size={11} className="ml-2" />
              <span>Filters:</span>
              {brand.length > 0 && <button onClick={() => setBrand([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Brand: {brand.length === 1 ? brand[0] : `${brand.length} brands`}</span><X size={10} /></button>}
              {size.length > 0 && <button onClick={() => setSize([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Size: {size.length === 1 ? size[0] : `${size.length} sizes`}</span><X size={10} /></button>}
              {godown.length > 0 && <button onClick={() => setGodown([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>Godown: {godown.length === 1 ? godown[0] : `${godown.length} godowns`}</span><X size={10} /></button>}
              {partyIds.length > 0 && <button onClick={() => setPartyIds([])} className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-sm hover:bg-gray-200"><span>{partyIds.length === 1 ? (parties.find((p) => p.id === partyIds[0])?.name || "?") : `${partyIds.length} customers`}</span><X size={10} /></button>}
              <button onClick={clearFilters} className="text-[11px] font-semibold text-[#B91C1C] hover:underline" data-testid="sc-clear">Clear all</button>
            </>
          )}
        </div>
      </div>

      {/* Big KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70">Total sales</div>
          <div className="data-num text-3xl font-black mt-1" data-testid="sc-kpi-amount">₹ {formatMoney(summary.amount || 0)}</div>
          <div className="text-[10px] text-white/70 mt-1">{summary.count || 0} bills</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Total KG (LPG)</div>
          <div className="data-num text-3xl font-black mt-1 text-[#B45309]" data-testid="sc-kpi-kg">{formatMoney(summary.kg || 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Filled cylinders</div>
          <div className="data-num text-2xl mt-1" data-testid="sc-kpi-filled">{formatMoney(summary.filled_qty || 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">MT cylinders</div>
          <div className="data-num text-2xl mt-1" data-testid="sc-kpi-mt">{formatMoney(summary.mt_qty || 0)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Avg ₹ / kg</div>
          <div className="data-num text-2xl mt-1" data-testid="sc-kpi-rpk">{summary.rate_per_kg ? `₹ ${summary.rate_per_kg}` : "—"}</div>
        </div>
      </div>

      {/* Group table */}
      {groupBy !== "none" && (
        <div className="card-flat" data-testid="sc-group-block">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="small-label text-gray-500">Breakdown by</div>
              <div className="font-heading font-black text-lg">{groupLabel}</div>
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search group…" className="input-lg h-9 text-sm pl-8 w-56"
                data-testid="sc-group-search" />
            </div>
          </div>
          <div className="table-wrap">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <th className="text-left">{groupLabel}</th>
                  <th className="num">Bills</th>
                  <th className="num">Filled</th>
                  <th className="num">MT</th>
                  <th className="num">KG</th>
                  <th className="num">₹ / kg</th>
                  <th className="num">Amount ₹</th>
                  <th className="num">Share</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="8" className="py-8 text-center text-gray-400 text-sm">Loading…</td></tr>}
                {!loading && groups.length === 0 && (
                  <tr><td colSpan="8" className="py-8 text-center text-gray-400 text-sm">No sales in this window.</td></tr>
                )}
                {!loading && groups.map((g) => {
                  const share = summary.amount > 0 ? (g.amount / summary.amount) * 100 : 0;
                  return (
                    <tr key={g.key} data-testid={`sc-group-${g.key}`}>
                      <td className="font-semibold">{g.key}</td>
                      <td className="num text-xs text-gray-500">{g.bills}</td>
                      <td className="num data-num text-[#15803D] font-bold">{g.filled_qty || ""}</td>
                      <td className="num data-num text-[#B45309] font-bold">{g.mt_qty || ""}</td>
                      <td className="num data-num">{formatMoney(g.kg)}</td>
                      <td className="num data-num text-xs">₹ {g.rate_per_kg}</td>
                      <td className="num data-num font-bold">₹ {formatMoney(g.amount)}</td>
                      <td className="num">
                        <div className="flex items-center gap-2 justify-end">
                          <span className="text-xs text-gray-500 w-10 text-right">{share.toFixed(1)}%</span>
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-black" style={{ width: `${Math.min(share, 100)}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-gray-500 p-3 border-t border-gray-100">
            Aggregates cover only line items that match the active brand / size filter.
            <Link to="/logs/sales" className="ml-2 underline font-semibold">Open full sales log →</Link>
          </p>
        </div>
      )}
    </div>
  );
}
